<template>
  <div class="forward-settlement-container">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>结算转发</span>
      </div>
      <div class="header-info">
        <span class="settlement-info">
          原结算单：{{ originalSettlement.settlementNumber || '-' }}
        </span>
        <span class="settlement-info">
          标题：{{ originalSettlement.settlementTitle || '-' }}
        </span>
      </div>
    </div>

    <!-- 转发表单 -->
    <el-card class="form-card">
      <div slot="header" class="card-header">
        <span>转发信息</span>
      </div>
      
      <el-form
        ref="forwardForm"
        :model="forwardForm"
        :rules="forwardRules"
        label-width="120px"
        class="forward-form"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="新需求方" prop="newDemandUnitIds">
              <el-select
                v-model="forwardForm.newDemandUnitIds"
                placeholder="请选择新需求方"
                multiple
                filterable
                style="width: 100%"
                @change="handleDemandUnitChange"
              >
                <el-option
                  v-for="unit in demandUnits"
                  :key="unit.id"
                  :label="unit.name"
                  :value="unit.id"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
          
          <el-col :span="12">
            <el-form-item label="价格浮动比例" prop="markupRate">
              <el-input-number
                v-model="forwardForm.markupRate"
                :min="-100"
                :max="100"
                :precision="2"
                placeholder="请输入浮动比例"
                style="width: 100%"
                @change="handleMarkupRateChange"
              >
                <template slot="append">%</template>
              </el-input-number>
              <div class="form-tip">
                <i class="el-icon-info"></i>
                输入40表示上浮40%，输入-10表示下调10%
              </div>
            </el-form-item>
          </el-col>
        </el-row>


        <el-row>
          <el-col :span="24">
            <el-form-item label="备注" prop="remark">
              <el-input
                v-model="forwardForm.remark"
                type="textarea"
                :rows="3"
                placeholder="请输入转发备注"
                maxlength="500"
                show-word-limit
              ></el-input>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 结算项目列表 -->
    <el-card class="items-card">
      <div slot="header" class="card-header">
        <span>结算项目（价格已按浮动比例调整）</span>
        <span class="price-summary">
          原总金额：{{ formatAmount(originalTotalAmount) }} → 
          新总金额：{{ formatAmount(newTotalAmount) }}
          <span v-if="markupDifference !== 0" :class="markupDifference > 0 ? 'price-increase' : 'price-decrease'">
            ({{ markupDifference > 0 ? '+' : '' }}{{ formatAmount(markupDifference) }})
          </span>
        </span>
      </div>

      <el-table
        :data="settlementItems"
        border
        stripe
        style="width: 100%"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column
          label="产品名称"
          min-width="150"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.materialsName || scope.row.productName || '-' }}
          </template>
        </el-table-column>

        <el-table-column
          label="规格型号"
          min-width="120"
          align="center"
        >
          <template slot-scope="scope">
            <el-tooltip
              v-if="getSpecificationText(scope.row)"
              :content="getSpecificationText(scope.row)"
              placement="top"
              effect="dark"
              popper-class="spec-tooltip"
            >
              <span>{{ truncateText(getSpecificationText(scope.row), 20) }}</span>
            </el-tooltip>
            <span v-else>-</span>
          </template>
        </el-table-column>

        <el-table-column
          label="单位"
          width="80"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.unit || scope.row.itemUnit || '-' }}
          </template>
        </el-table-column>

        <el-table-column
          label="结算数量"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            {{ scope.row.settledQuantity || 0 }}
          </template>
        </el-table-column>

        <el-table-column
          label="原单价"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatUnitPrice(scope.row.originalUnitPrice || scope.row.unitPrice || 0) }}
          </template>
        </el-table-column>

        <el-table-column
          label="新单价"
          width="100"
          align="center"
        >
          <template slot-scope="scope">
            <span :class="getPriceChangeClass()">
              {{ formatUnitPrice(scope.row.newUnitPrice || scope.row.unitPrice || 0) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          label="原金额"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatAmount(scope.row.originalAmount || scope.row.settledAmount || 0) }}
          </template>
        </el-table-column>

        <el-table-column
          label="新金额"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            <span :class="getPriceChangeClass()">
              {{ formatAmount(scope.row.newAmount || scope.row.settledAmount || 0) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column
          label="税率"
          width="80"
          align="center"
        >
          <template slot-scope="scope">
            {{ (scope.row.taxRate || 0).toFixed(1) }}%
          </template>
        </el-table-column>

        <el-table-column
          label="原总金额"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            {{ formatAmount(scope.row.originalTotalAmount || scope.row.totalAmount || 0) }}
          </template>
        </el-table-column>

        <el-table-column
          label="新总金额"
          width="120"
          align="center"
        >
          <template slot-scope="scope">
            <span :class="getPriceChangeClass()">
              {{ formatAmount(scope.row.newTotalAmount || scope.row.totalAmount || 0) }}
            </span>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 操作按钮 -->
    <div class="action-buttons">
      <el-button @click="goBack">取消</el-button>
      <el-button v-permission="'settlement:forward-settlement:forward'" type="primary" @click="submitForward" :loading="submitting">
        确认转发
      </el-button>
    </div>
  </div>
</template>

<script>
import { query_settlement_by_id, forward_settlement } from '@/api/modules/settlement'

export default {
  name: 'ForwardSettlement',
  data() {
    return {
      // 原结算单信息
      originalSettlement: {},
      // 转发表单
      forwardForm: {
        newDemandUnitIds: [],
        markupRate: 0,
        remark: ''
      },
      // 表单验证规则
      forwardRules: {
        newDemandUnitIds: [
          { required: true, message: '请选择新需求方', trigger: 'change' }
        ],
        markupRate: [
          { required: true, message: '请输入价格浮动比例', trigger: 'blur' }
        ]
      },
      // 需求方单位列表
      demandUnits: [],
      // 结算项目列表
      settlementItems: [],
      // 原总金额
      originalTotalAmount: 0,
      // 新总金额
      newTotalAmount: 0,
      // 价格差异
      markupDifference: 0,
      // 提交状态
      submitting: false
    };
  },
  created() {
    this.loadSettlementData();
    this.loadDemandUnits();
  },
  methods: {
    // 获取规格型号文本
    getSpecificationText(item) {
      if (!item) return '';
      return (
        item.materialsSpec ||
        item.specification ||
        item.itemSpecification ||
        ''
      );
    },

    // 截断文本显示，默认为20个字符
    truncateText(text, length = 20) {
      if (!text) return '-';
      const str = String(text);
      return str.length > length ? `${str.slice(0, length)}...` : str;
    },

    // 加载结算数据
    async loadSettlementData() {
      const settlementId = this.$route.params.id;
      if (!settlementId) {
        this.$message.error('缺少结算ID参数');
        this.goBack();
        return;
      }

      try {
        const response = await query_settlement_by_id(settlementId);
        if (response.data && response.data.success) {
          this.originalSettlement = response.data.data;
          
          // 初始化转发表单
          this.forwardForm.remark = `转发自结算单：${this.originalSettlement.settlementNumber || ''}`;
          
          // 处理结算项目
          this.processSettlementItems();
        } else {
          this.$message.error(response.data?.message || '获取结算数据失败');
          this.goBack();
        }
      } catch (error) {
        console.error('加载结算数据失败:', error);
        this.$message.error('加载结算数据失败，请稍后重试');
        this.goBack();
      }
    },

    // 加载需求方单位列表
    async loadDemandUnits() {
      try {
        // 使用通用的HTTP请求获取单位列表
        const response = await this.$http.get('/units');
        if (response.data && response.data.success) {
          this.demandUnits = response.data.data || [];
        } else {
          console.error('获取需求方单位失败:', response.data?.message);
          this.$message.warning('获取需求方单位失败');
        }
      } catch (error) {
        console.error('获取需求方单位失败:', error);
        // 如果API不存在，使用模拟数据
        this.demandUnits = [
          { id: 1, name: '需求方单位A' },
          { id: 2, name: '需求方单位B' },
          { id: 3, name: '需求方单位C' }
        ];
        this.$message.warning('使用模拟需求方单位数据');
      }
    },

    // 处理结算项目
    processSettlementItems() {
      if (!this.originalSettlement.settlementItems || this.originalSettlement.settlementItems.length === 0) {
        this.settlementItems = [];
        return;
      }

      this.settlementItems = this.originalSettlement.settlementItems.map(item => {
        const processedItem = { ...item };
        
        // 保存原始价格信息
        processedItem.originalUnitPrice = item.unitPrice || 0;
        processedItem.originalAmount = item.settledAmount || 0;
        processedItem.originalTotalAmount = item.totalAmount || 0;
        
        // 计算新价格
        this.calculateNewPrices(processedItem);
        
        return processedItem;
      });

      this.calculateTotalAmounts();
    },

    // 计算新价格
    calculateNewPrices(item) {
      const markupRate = this.forwardForm.markupRate || 0;
      const multiplier = 1 + (markupRate / 100);
      
      // 计算新单价
      item.newUnitPrice = Number((item.originalUnitPrice * multiplier).toFixed(5));
      
      // 计算新金额（不含税）
      item.newAmount = Number((item.originalAmount * multiplier).toFixed(2));
      
      // 计算新总金额（含税）
      const taxAmount = item.newAmount * (item.taxRate || 0);
      item.newTotalAmount = Number((item.newAmount + taxAmount).toFixed(2));
    },

    // 计算总金额
    calculateTotalAmounts() {
      this.originalTotalAmount = this.settlementItems.reduce((sum, item) => {
        return sum + (item.originalTotalAmount || 0);
      }, 0);
      
      this.newTotalAmount = this.settlementItems.reduce((sum, item) => {
        return sum + (item.newTotalAmount || 0);
      }, 0);
      
      this.markupDifference = this.newTotalAmount - this.originalTotalAmount;
    },

    // 处理需求方变更
    handleDemandUnitChange() {
      // 可以在这里添加需求方变更的逻辑
    },

    // 处理价格浮动比例变更
    handleMarkupRateChange() {
      this.processSettlementItems();
    },

    // 获取价格变更样式类
    getPriceChangeClass() {
      const markupRate = this.forwardForm.markupRate || 0;
      if (markupRate > 0) {
        return 'price-increase';
      } else if (markupRate < 0) {
        return 'price-decrease';
      }
      return '';
    },

    // 格式化金额（带千位分隔符）
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00';
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    // 格式化单价（固定保留5位小数）
    formatUnitPrice(amount) {
      if (amount === undefined || amount === null) return '0.00000';
      const num = Number(amount);
      return isNaN(num) ? '0.00000' : num.toFixed(5);
    },

    // 提交转发
    async submitForward() {
      try {
        await this.$refs.forwardForm.validate();
        
        this.submitting = true;
        
        // 构建转发数据 - 根据API文档调整参数结构
        const forwardData = {
          newSupplierId: this.forwardForm.newDemandUnitIds[0], // 使用第一个选择的需求方作为新供应商
          markupRate: this.forwardForm.markupRate / 100, // 转换为小数形式
          forwardRemark: this.forwardForm.remark
        };

        // 调用转发API
        const response = await forward_settlement(this.originalSettlement.id, forwardData);
        
        if (response.data && response.data.success) {
          this.$message.success('结算转发成功');
          this.goBack();
        } else {
          this.$message.error(response.data?.message || '结算转发失败');
        }
      } catch (error) {
        console.error('结算转发失败:', error);
        if (error.message) {
          this.$message.error(error.message);
        } else {
          this.$message.error('结算转发失败，请稍后重试');
        }
      } finally {
        this.submitting = false;
      }
    },

    // 返回
    goBack() {
      this.$router.go(-1);
    }
  }
};
</script>

<style lang="scss" scoped>
.forward-settlement-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: 100vh;

  .page-header {
    margin-bottom: 20px;
    background-color: #fff;
    padding: 20px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .header-title {
      display: flex;
      align-items: center;
      margin-bottom: 10px;

      .title-mark {
        width: 4px;
        height: 18px;
        background-color: #2c73eb;
        margin-right: 8px;
      }

      span {
        font-size: 18px;
        font-weight: 500;
        color: #303133;
      }
    }

    .header-info {
      display: flex;
      gap: 20px;

      .settlement-info {
        color: #606266;
        font-size: 14px;
      }
    }
  }

  .form-card {
    margin-bottom: 20px;

    .card-header {
      font-size: 16px;
      font-weight: 500;
      color: #303133;
    }

    .forward-form {
      .el-form-item {
        margin-bottom: 20px;
      }
    }
  }

  .items-card {
    margin-bottom: 20px;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 16px;
      font-weight: 500;
      color: #303133;

      .price-summary {
        font-size: 14px;
        font-weight: normal;
        color: #606266;

        .price-increase {
          color: #f56c6c;
          font-weight: bold;
        }

        .price-decrease {
          color: #67c23a;
          font-weight: bold;
        }
      }
    }
  }

  .action-buttons {
    text-align: center;
    padding: 20px;
    background-color: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .el-button {
      margin: 0 10px;
      min-width: 100px;
    }
  }

  .price-increase {
    color: #f56c6c;
    font-weight: bold;
  }

  .price-decrease {
    color: #67c23a;
    font-weight: bold;
  }

  .form-tip {
    margin-top: 5px;
    font-size: 12px;
    color: #909399;
    display: flex;
    align-items: center;

    i {
      margin-right: 4px;
      color: #409EFF;
    }
  }
}
</style>

<style lang="scss">
.spec-tooltip {
  max-width: 600px !important;
  white-space: normal;
  word-break: break-all;
}
</style>
